import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cta } from "@/lib/site";
import { ArrowUpRight } from "lucide-react";

const FAQS = [
  {
    q: "What is a postcard mailing service?",
    a: "A postcard mailing service prints your postcard, applies postage, and delivers it to a list of recipients on your behalf. Postmark & Co. is a full postcard printing and mailing service in the United States — you upload a design and a mailing list, and we handle bulk postcard printing, stamping, and USPS delivery, usually within 48 hours.",
  },
  {
    q: "How much does postcard marketing cost?",
    a: "Postcards start at $0.90 per piece, postage included. Bulk postcard printing and mass postcard mailing volumes lower the per-piece cost. There are no minimums, no contracts, and no setup fees — your first postcard is free.",
  },
  {
    q: "How long does it take to print and mail a postcard?",
    a: "Most postcards are printed, stamped, and dropped in the mail within 48 hours of order. USPS First-Class delivery typically arrives at U.S. addresses in 2–5 business days after mailing — this is one of the fastest postcard delivery solutions available nationwide.",
  },
  {
    q: "Do you offer EDDM (Every Door Direct Mail) postcard service?",
    a: "Yes. Our EDDM postcard service and EDDM marketing service deliver your postcard to every address in a targeted postal route or neighborhood — perfect for local postcard marketing, restaurant openings, retail promotions, and real estate farm mailings. EDDM printing and mailing is fully managed end-to-end.",
  },
  {
    q: "Can I get a targeted mailing list?",
    a: "Yes. Our mailing list service and mailing list targeting let you filter by geography (city, ZIP code, radius), demographics (income, homeowner status, age), and industry. Address list processing, deduplication, and CASS certification are included so every card lands in a real, deliverable U.S. mailbox.",
  },
  {
    q: "Is postcard marketing effective in 2026?",
    a: "Yes. Postcard marketing consistently outperforms email in response rate — the U.S. Data & Marketing Association reports 4–7× higher response for direct mail versus email. Handwritten-style postcards get opened, read, and remembered because they bypass spam filters, ad blockers, and algorithmic feeds.",
  },
  {
    q: "Who uses postcard marketing?",
    a: "Postcard marketing for realtors and insurance agents (farm mailings, lead generation). Postcard marketing for restaurants, retail stores, and service businesses (grand openings, promotions, coupons, offers). Postcard marketing for nonprofits (donor outreach). Postcard marketing for startups and events (launches, invitations). Basically anyone who needs targeted postcard marketing that people actually read.",
  },
  {
    q: "Do postcards come with tracking and analytics?",
    a: "Yes. Postcard tracking service and postcard analytics tools are included — see when each piece is printed, mailed, and delivered. Analytics show delivery rates, response codes, and QR code scans so you can measure the ROI of every postcard campaign.",
  },
  {
    q: "Can I automate postcard campaigns?",
    a: "Yes. Postcard workflow automation and postcard campaign management let you trigger postcards from your CRM, e-commerce platform, or Zapier — new lead, closed deal, abandoned cart, birthday, or renewal. Includes scheduled drops, drip sequences, and A/B testing.",
  },
  {
    q: "Do you print custom postcard designs?",
    a: "Yes. Custom postcard printing and postcard design and print services support full-bleed 4×6, 5×7, and 6×11 formats, glossy or matte finish, with variable data (personalized names, offers, QR codes) on every card. Affordable postcard printing with premium finish quality.",
  },
];

export const Faq = () => {
  return (
    <section
      id="faq"
      className="relative py-24 md:py-36 border-t border-[#E4DFD4]"
      data-testid="faq-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-12 gap-10">
        {/* Left rail */}
        <div className="col-span-12 md:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="postmark text-[#2B3A55] mb-6">
              <span className="w-1 h-1 rounded-full bg-[#2B3A55]" />
              Postage Due — FAQ
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight text-[#1C1C1A] mb-6">
              Everything you wanted to know about the modern{" "}
              <span className="italic text-[#D93829]">
                postcard marketing service
              </span>
              .
            </h2>
            <p className="text-[#3B3A36] text-lg font-sans-body leading-relaxed mb-8">
              Bulk postcard printing, mass postcard mailing, EDDM, targeted
              mailing lists, tracking, and campaign automation — nationwide,
              across all 50 states.
            </p>

            <a
              href={cta("faq")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="faq-cta"
              className="group inline-flex items-center gap-3 bg-[#1C1C1A] text-[#F7F5F0] px-8 py-4 rounded-full text-sm uppercase tracking-[0.2em] font-semibold hover:bg-[#D93829] transition-colors duration-500"
            >
              Ask by sending one
              <ArrowUpRight
                size={16}
                className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
              />
            </a>
          </motion.div>
        </div>

        {/* Accordion */}
        <div className="col-span-12 md:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          >
            <Accordion
              type="single"
              collapsible
              className="w-full"
              defaultValue="item-0"
              data-testid="faq-accordion"
            >
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="border-b border-[#D5D0C5]"
                  data-testid={`faq-item-${i}`}
                >
                  <AccordionTrigger className="text-left font-serif-display text-lg md:text-xl text-[#1C1C1A] hover:no-underline hover:text-[#D93829] py-6">
                    <span className="pr-6">{f.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-[#3B3A36] text-base md:text-lg font-sans-body leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>

      {/* Hidden-but-crawlable use-case list (semantic keyword coverage) */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 mt-20 border-t border-[#E4DFD4] pt-10">
        <h3 className="text-[11px] uppercase tracking-[0.24em] text-[#68655E] mb-6">
          Postcard marketing solutions we cover
        </h3>
        <ul className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-[13px] text-[#3B3A36] font-sans-body">
          <li>Postcard printing service</li>
          <li>Postcard mailing service</li>
          <li>Postcard marketing service</li>
          <li>Bulk postcard printing</li>
          <li>Mass postcard mailing</li>
          <li>Custom postcard printing</li>
          <li>Affordable postcard printing</li>
          <li>Low cost postcard mailing</li>
          <li>Targeted postcard marketing</li>
          <li>Business postcard mailers</li>
          <li>Neighborhood postcard mailers</li>
          <li>Local postcard marketing</li>
          <li>Postcard design and print</li>
          <li>Postcard delivery service</li>
          <li>Postcard fulfillment service</li>
          <li>Postcard distribution service</li>
          <li>Postcard promotions &amp; coupons</li>
          <li>Print and mail postcards</li>
          <li>Postcard tracking service</li>
          <li>Postcard analytics tools</li>
          <li>Postcard workflow automation</li>
          <li>Postcard campaign management</li>
          <li>Mailing list service</li>
          <li>Address list processing</li>
          <li>EDDM postcard service</li>
          <li>EDDM marketing service</li>
          <li>EDDM printing and mailing</li>
          <li>Postcard lead generation</li>
          <li>Postcard marketing for realtors</li>
          <li>Postcard marketing for insurance agents</li>
          <li>Postcard marketing for restaurants</li>
          <li>Postcard marketing for small business</li>
          <li>Postcard marketing for retail stores</li>
          <li>Postcard marketing for service businesses</li>
          <li>Postcard marketing for startups</li>
          <li>Postcard marketing for nonprofits</li>
          <li>Postcard marketing for events</li>
          <li>Physical mail marketing</li>
          <li>Print and ship postcards</li>
          <li>Postcard outreach service</li>
        </ul>
      </div>
    </section>
  );
};

export default Faq;
